import { useEffect, useRef } from "react";
import { useChatContext } from "../context";
import { useChatTurn } from "../hooks/useChatTurn";
import { useImageAttachments } from "../hooks/useImageAttachments";
import { isAIMessage } from "../utils";
import ChatInput, { ChatInputHandle } from "../components/ChatInput";
import MessageList from "../components/MessageList";
import WhistleIcon from "./WhistleIcon";

// Copy placeholders — see the spec's "Copy placeholders" table before shipping.
const EMPTY_STATE_TITLE = "Ask Whistle AI anything about your team's data.";
const EXAMPLE_PROMPTS = [
  "Who was my fastest player today?",
  "What are the team's key metrics for this week?",
  "Summarize yesterday's session.",
];

interface FloatingChatPanelProps {
  open: boolean;
  onClose: () => void;
  onCaptureOpenChange?: (open: boolean) => void;
  capturing?: boolean;
}

const FloatingChatPanel: React.FC<FloatingChatPanelProps> = ({
  open, onClose, onCaptureOpenChange, capturing,
}) => {
  const { messages, isTyping, conversationEpoch, markMessagesSeen } = useChatContext();
  const { sendMessage } = useChatTurn();
  const { attachments, setAttachments, attachmentsRef, stageFiles, handleRemove } =
    useImageAttachments(conversationEpoch, isTyping);
  const panelRef = useRef<HTMLDivElement>(null);
  // An answer from a message row goes through the composer, which owns the draft and the send.
  const inputRef = useRef<ChatInputHandle>(null);

  // Focus the composer when the panel opens. The composer renders CodeMirror,
  // whose editable surface is a contenteditable div.cm-content, not a textarea.
  // Only on a real closed→open transition: after navigating back from a hidden
  // route the panel remounts with open already true, and focusing then would
  // steal keyboard focus from the page. The ref initializes to the mount-time
  // value of `open`, so a remount-while-open never counts as a transition.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      panelRef.current?.querySelector<HTMLElement>(".cm-content")?.focus();
    }
    prevOpenRef.current = open;
  }, [open]);

  // Reveal animations key off message.fresh at MessageList mount. The panel
  // unmounts MessageList on close, so stale flags would replay every count-up
  // and chart animation on reopen — mark everything seen when the panel closes.
  useEffect(() => {
    if (!open) markMessagesSeen();
  }, [open, markMessagesSeen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // While a screenshot capture is open, Escape belongs to ScreengrabCapture's
    // cancel/retake flow — the widget is visibility:hidden then anyway.
    // !e.defaultPrevented: CodeMirror's autocomplete calls preventDefault() on
    // Escape when it closes its own suggestion popup, without stopping
    // propagation — the panel must only close on an Escape nothing else handled.
    if (e.key === "Escape" && !capturing && !e.defaultPrevented) {
      onClose();
      (document.querySelector(".floating-chat-fab") as HTMLElement | null)?.focus();
    }
  };

  if (!open) return null;

  // Only the seeded intro (and any assistant messages) so far — show starter prompts.
  const showPrompts = messages.every(isAIMessage);

  return (
    <div
      className="floating-chat-panel"
      role="dialog"
      aria-label="Whistle AI"
      ref={panelRef}
      onKeyDown={handleKeyDown}
    >
      <div className="floating-chat-header">
        <span className="floating-chat-header-title">
          <WhistleIcon /> Whistle AI
        </span>
        <button
          type="button"
          className="floating-chat-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* A drop on the message list stages, the same as the drop zone around MessageList on the
          chat page (no overlay here). ChatInput's window guard covers drops everywhere else. */}
      <div
        className="floating-chat-messages"
        onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) e.preventDefault(); }}
        onDrop={(e) => {
          if (e.dataTransfer.files.length === 0) return;
          e.preventDefault();
          stageFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <MessageList onAnswer={r => inputRef.current?.answer(r)} />
        {showPrompts && (
          <div className="floating-chat-empty">
            <div className="floating-chat-empty-title">{EMPTY_STATE_TITLE}</div>
            <div className="floating-chat-empty-prompts">
              {EXAMPLE_PROMPTS.map(p => (
                <button
                  key={p}
                  type="button"
                  className="floating-chat-empty-prompt-btn"
                  // Prompts bypass ChatInput's send path, which is what drains staged
                  // attachments — sending with anything staged would silently drop the
                  // image now and misattach it to the next composer send.
                  disabled={isTyping || attachments.length > 0}
                  onClick={() => sendMessage(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="floating-chat-input-area">
        <ChatInput
          ref={inputRef}
          onSend={sendMessage}
          disabled={isTyping}
          conversationEpoch={conversationEpoch}
          attachments={attachments}
          setAttachments={setAttachments}
          attachmentsRef={attachmentsRef}
          stageFiles={stageFiles}
          handleRemove={handleRemove}
          onCaptureOpenChange={onCaptureOpenChange}
        />
      </div>
    </div>
  );
};

export default FloatingChatPanel;
